import React, {
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import { useFlutterWebView } from 'Src/hooks/useFlutterWebView';

import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import { EMAIL_KEY, KUESKI_SESSION_STORAGE_KEY } from 'Src/consts/session';
import { GlobalLoadingWrapper } from 'Utils/GlobalLoading/GlobalLoading.styles';
import { Spinner } from 'Redesign_Funnel/components/Loading';
import { sendHandleSessionExpiredEvent } from 'Utils/events/flutterWebView';

interface FlutterWebViewValidationProps {
  children: ReactNode
}

const logger = LoggerFactory.getLogger(DOMAIN.MOBILE);

const FlutterWebViewValidation = ({ children }: FlutterWebViewValidationProps) => {
  const {
    isReady,
    isLoading: isLoadingFlutterWebView,
    isMobileApp,
  } = useFlutterWebView();
  const [isLoading, setIsLoading] = useState(true);

  const sleep = (seconds = 0) => new Promise((resolve) => setTimeout(resolve, 1000 * seconds));

  const retry = async (retries = 3) => {
    if (retries <= 0) {
      logger.error(
        'FlutterInAppWebView retry sending the event',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        { retries },
      );
      throw new Error('FlutterInAppWebView retry sending the event');
    }
    logger.warn(
      'FlutterInAppWebView retry sending the event',
      `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
      { retries },
    );
    await sendHandleSessionExpiredEvent(); // retry sending the event
    await sleep(2); // Wait for the event redirect
    await retry(retries - 1); // retry again
  };

  const validateSession = async () => {
    const sessionId = localStorage.getItem(KUESKI_SESSION_STORAGE_KEY);
    const email = localStorage.getItem(EMAIL_KEY);
    const debugValues = {
      email: Boolean(email),
      isLoadingFlutterWebView,
      isMobileApp,
      isReady,
      sessionId: Boolean(sessionId),
    };

    if (sessionId && email) {
      logger.info(
        'FlutterInAppWebView has a session',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      setIsLoading(false);
    } else {
      logger.error(
        'FlutterInAppWebView does not have a session',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      await sendHandleSessionExpiredEvent();
      await sleep(2); // Wait for the event redirect
      await retry();
    }
  };

  useEffect(() => {
    if (!isMobileApp) setIsLoading(false);
    else if (isReady) validateSession();
    else setIsLoading(true);
  }, [isReady]);

  if (isLoading) {
    return (
      <GlobalLoadingWrapper aria-label="Cargando...">
        <Spinner />
      </GlobalLoadingWrapper>
    );
  }

  return children;
};

export default FlutterWebViewValidation;
